import { useRef, useState } from 'react';
import { Button, CompassLogo, cn } from '@jeraisonne/ui';
import { ChevronLeft, ChevronRight, Play } from 'lucide-react';
import {
  AXIS_LOCK_PX,
  nextIndex,
  prevIndex,
  resolveSwipe,
  slideOffsetPercent,
} from './carousel-nav';
import { DecouverteProofView } from './decouverte-proofs';
import {
  DECOUVERTE_HERO,
  DECOUVERTE_TRACKS,
  DECOUVERTE_TRACK_ORDER,
  type DecouverteSlide,
  type DecouverteTrack,
} from './decouverte-slides';
import type { DecouverteProfile } from './decouverte-profile';

/** Bleu nuit de la marque : l'accueil est sombre quel que soit le thème. */
const NIGHT = 'bg-[#061433]';

interface DecouverteCarouselProps {
  /** Nouveau venu : inscription, avec le profil choisi. */
  onSignup: (profile: DecouverteProfile) => void;
  /** Compte existant : connexion. */
  onLogin: () => void;
  /** Essai sans compte : la démo guidée. */
  onDemo: (profile: DecouverteProfile) => void;
  /** Appelé au choix d'un profil sur l'accueil (mémorisation, statistiques). */
  onPickProfile?: (profile: DecouverteProfile) => void;
  /** URL du site vitrine. Fournie → un lien discret « Visiter le site » sur l'accueil. */
  marketingUrl?: string;
  /** Rouvre directement le parcours d'un profil (sinon : l'accueil). */
  initialProfile?: DecouverteProfile | null;
}

/**
 * Accueil pré-login : d'abord la promesse et « Tu es… » (trois VRAIS boutons —
 * l'ancien écran « Pour qui ? » montrait des cartes non cliquables que les gens
 * touchaient au lieu de se connecter), puis le parcours du profil choisi.
 */
export function DecouverteCarousel({
  onSignup,
  onLogin,
  onDemo,
  onPickProfile,
  marketingUrl,
  initialProfile = null,
}: DecouverteCarouselProps) {
  const [profile, setProfile] = useState<DecouverteProfile | null>(initialProfile);

  return (
    // Mobile : plein écran (`h-dvh`). Desktop : colonne de largeur bornée dont la
    // hauteur cesse de croître avec l'écran (`sm:h-184`, plafonnée par `max-h-dvh`).
    <div
      className={cn(
        'sm:h-184 relative mx-auto flex h-dvh max-h-dvh w-full max-w-xl flex-col overflow-hidden sm:rounded-3xl lg:rounded-none',
        NIGHT,
      )}
    >
      {profile === null ? (
        <HeroScreen
          onPick={(p) => {
            onPickProfile?.(p);
            setProfile(p);
          }}
          onLogin={onLogin}
          marketingUrl={marketingUrl}
        />
      ) : (
        <TrackScreen
          key={profile}
          track={DECOUVERTE_TRACKS[profile]}
          onBack={() => setProfile(null)}
          onSignup={() => onSignup(profile)}
          onDemo={() => onDemo(profile)}
          onLogin={onLogin}
        />
      )}
    </div>
  );
}

// ─── Accueil ──────────────────────────────────────────────────────────────────

function HeroScreen({
  onPick,
  onLogin,
  marketingUrl,
}: {
  onPick: (profile: DecouverteProfile) => void;
  onLogin: () => void;
  marketingUrl?: string;
}) {
  return (
    <section aria-labelledby="decouverte-hero-title" className="relative flex h-full flex-col">
      <Photo src={DECOUVERTE_HERO.image} focus={DECOUVERTE_HERO.imageFocus} tall eager />

      <header className="relative z-10 flex items-center px-5 pt-[calc(env(safe-area-inset-top)+1rem)]">
        <CompassLogo size="sm" animated={false} wordmarkClassName="text-white" />
      </header>

      <div className="relative z-10 mt-auto px-5 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] sm:px-8">
        <Eyebrow>{DECOUVERTE_HERO.eyebrow}</Eyebrow>
        <h1
          id="decouverte-hero-title"
          className="font-display mt-2 text-[2.1rem] font-extrabold leading-[1.05] text-white sm:text-[2.6rem]"
        >
          {DECOUVERTE_HERO.title}{' '}
          <span className="text-[#7fb6ff]">{DECOUVERTE_HERO.highlight}</span>
        </h1>
        <p className="mt-2.5 max-w-sm text-[15px] leading-snug text-white/80">
          {DECOUVERTE_HERO.body}
        </p>

        <p
          id="decouverte-profile-question"
          className="font-display mt-4 text-sm font-bold uppercase tracking-[0.14em] text-white/70"
        >
          {DECOUVERTE_HERO.question}
        </p>
        <div
          role="group"
          aria-labelledby="decouverte-profile-question"
          className="mt-2 flex flex-col gap-1.5"
        >
          {DECOUVERTE_TRACK_ORDER.map((p) => {
            const track = DECOUVERTE_TRACKS[p];
            return (
              <button
                key={p}
                type="button"
                onClick={() => onPick(p)}
                className="flex min-h-[3.25rem] w-full items-center gap-3 rounded-2xl border border-white/25 bg-white/[0.14] px-3.5 py-1.5 text-left backdrop-blur-md transition hover:bg-white/[0.18] active:scale-[0.98]"
              >
                <span
                  aria-hidden
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/15 text-lg"
                >
                  {track.emoji}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="font-display block text-base font-bold text-white">
                    {track.label}
                  </span>
                  <span className="block truncate text-[13px] text-white/70">{track.hint}</span>
                </span>
                <ChevronRight aria-hidden className="h-5 w-5 shrink-0 text-white/80" />
              </button>
            );
          })}
        </div>

        <div className="mt-2 flex flex-col items-center">
          <button
            type="button"
            onClick={onLogin}
            className="min-h-11 px-3 text-sm text-white/75 transition-colors hover:text-white"
          >
            J’ai déjà un compte · <span className="font-semibold text-white">Se connecter</span>
          </button>
          {marketingUrl && (
            <a
              href={marketingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="py-1 text-xs font-medium text-white/50 transition-colors hover:text-white/80"
            >
              Visiter le site
            </a>
          )}
        </div>
      </div>
    </section>
  );
}

// ─── Parcours d'un profil ─────────────────────────────────────────────────────

function TrackScreen({
  track,
  onBack,
  onSignup,
  onDemo,
  onLogin,
}: {
  track: DecouverteTrack;
  onBack: () => void;
  onSignup: () => void;
  onDemo: () => void;
  onLogin: () => void;
}) {
  const slides = track.slides;
  const count = slides.length;

  const [index, setIndex] = useState(0);
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const axisRef = useRef<'x' | 'y' | null>(null);

  const isLast = index === count - 1;

  function goNext() {
    setIndex((i) => nextIndex(i, count));
  }
  function goPrev() {
    setIndex((i) => prevIndex(i));
  }

  function handlePointerDown(event: React.PointerEvent<HTMLDivElement>) {
    if (!event.isPrimary) return;
    startRef.current = { x: event.clientX, y: event.clientY };
    axisRef.current = null;
    setDragging(true);
    const target = event.currentTarget;
    if (typeof target.setPointerCapture === 'function') {
      try {
        target.setPointerCapture(event.pointerId);
      } catch {
        /* Capture indisponible — le drag reste fonctionnel. */
      }
    }
  }

  function handlePointerMove(event: React.PointerEvent<HTMLDivElement>) {
    const start = startRef.current;
    if (!start) return;
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    // Verrouille l'axe au premier mouvement franc : un geste vertical ne fait
    // jamais glisser la piste.
    if (!axisRef.current) {
      if (Math.abs(dx) < AXIS_LOCK_PX && Math.abs(dy) < AXIS_LOCK_PX) return;
      axisRef.current = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
    }
    if (axisRef.current !== 'x') return;
    setDragX(dx);
  }

  function handlePointerEnd(event: React.PointerEvent<HTMLDivElement>) {
    const start = startRef.current;
    const axis = axisRef.current;
    startRef.current = null;
    axisRef.current = null;
    setDragging(false);
    setDragX(0);
    const target = event.currentTarget;
    if (typeof target.releasePointerCapture === 'function') {
      try {
        target.releasePointerCapture(event.pointerId);
      } catch {
        /* Rien à relâcher. */
      }
    }
    if (!start || axis !== 'x') return;
    setIndex((i) => resolveSwipe(event.clientX - start.x, i, count));
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      goPrev();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      goNext();
    }
  }

  return (
    <div className="relative flex h-full flex-col">
      {/* Barre du haut, posée sur la photo. */}
      <div className="absolute inset-x-0 top-0 z-20 flex items-center justify-between px-3 pt-[calc(env(safe-area-inset-top)+0.5rem)]">
        <button
          type="button"
          onClick={index === 0 ? onBack : goPrev}
          aria-label={index === 0 ? 'Changer de profil' : 'Diapositive précédente'}
          className="flex h-11 w-11 items-center justify-center rounded-full bg-[#061433]/45 text-white backdrop-blur-md transition active:scale-95"
        >
          <ChevronLeft aria-hidden className="h-5 w-5" />
        </button>
        {!isLast && (
          <button
            type="button"
            onClick={() => setIndex(count - 1)}
            className="min-h-11 rounded-full bg-[#061433]/45 px-4 text-sm font-semibold text-white backdrop-blur-md transition active:scale-95"
          >
            Passer
          </button>
        )}
      </div>

      <div
        role="region"
        aria-roledescription="carrousel"
        aria-label={`Présentation de JeRaisonne — ${track.label}`}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onPointerCancel={handlePointerEnd}
        className="relative flex-1 touch-pan-y overflow-hidden focus-visible:outline-none"
      >
        <div
          className={cn(
            // `absolute inset-0` et non `h-full` : la région tire sa hauteur de
            // `flex-1`, un `height: 100%` enfant retomberait sur son contenu.
            'absolute inset-0 flex',
            dragging
              ? 'transition-none'
              : 'motion-safe:transition-transform motion-safe:duration-300',
          )}
          style={{ transform: `translateX(calc(${slideOffsetPercent(index)}% + ${dragX}px))` }}
        >
          {slides.map((slide, i) => (
            <section
              key={slide.id}
              role="group"
              aria-roledescription="diapositive"
              aria-label={`${i + 1} sur ${count}`}
              aria-hidden={i !== index}
              inert={i !== index || undefined}
              className="flex h-full w-full shrink-0 select-none flex-col"
            >
              <TrackSlide slide={slide} near={Math.abs(i - index) <= 1} />
            </section>
          ))}
        </div>
      </div>

      <p aria-live="polite" className="sr-only">
        Diapositive {index + 1} sur {count}
      </p>

      <footer className="relative z-10 px-5 pb-[calc(env(safe-area-inset-bottom)+0.5rem)] pt-1 sm:px-8">
        <div className="mb-2 flex items-center justify-center">
          {slides.map((slide, i) => (
            <button
              key={slide.id}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={`Aller à la diapositive ${i + 1}`}
              aria-current={i === index ? 'true' : undefined}
              className="inline-flex h-9 w-9 items-center justify-center"
            >
              <span
                className={cn(
                  'h-2 rounded-full transition-all duration-200',
                  i === index ? 'w-6 bg-[#f7d068]' : 'w-2 bg-white/40',
                )}
              />
            </button>
          ))}
        </div>

        <div className="mx-auto w-full max-w-sm">
          {isLast ? (
            <Button
              variant="gold"
              size="lg"
              fullWidth
              onClick={onSignup}
              className="min-h-12 active:scale-[0.98]"
            >
              {track.cta}
            </Button>
          ) : (
            <Button size="lg" fullWidth onClick={goNext} className="min-h-12 active:scale-[0.98]">
              Continuer
              <ChevronRight aria-hidden className="h-5 w-5" />
            </Button>
          )}

          <div className="mt-1 flex items-center justify-center gap-1 text-sm">
            <button
              type="button"
              onClick={onDemo}
              className="inline-flex min-h-11 items-center gap-1.5 px-2 font-semibold text-[#9cc8ff] transition-colors hover:text-white"
            >
              <Play aria-hidden className="h-3.5 w-3.5 fill-current" />
              {track.demoLabel}
            </button>
            <span aria-hidden className="text-white/30">
              ·
            </span>
            <button
              type="button"
              onClick={onLogin}
              className="min-h-11 px-2 text-white/70 transition-colors hover:text-white"
            >
              Se connecter
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}

function TrackSlide({ slide, near }: { slide: DecouverteSlide; near: boolean }) {
  return (
    <>
      <div className="relative min-h-[13rem] flex-1">
        <Photo src={slide.image} focus={slide.imageFocus} eager={near} />
        <div className="absolute inset-x-0 bottom-2 z-10 flex justify-center">
          <DecouverteProofView proof={slide.proof} />
        </div>
      </div>
      <div className="px-5 pt-4 sm:px-8">
        <Eyebrow>{slide.eyebrow}</Eyebrow>
        <h2 className="font-display mt-1.5 text-balance text-[1.75rem] font-extrabold leading-[1.08] text-white sm:text-[2rem]">
          {slide.title} <span className="text-[#7fb6ff]">{slide.highlight}</span>
        </h2>
        <p className="mt-2 text-[15px] leading-snug text-white/80">{slide.body}</p>
      </div>
    </>
  );
}

// ─── Briques ──────────────────────────────────────────────────────────────────

function Eyebrow({ children }: { children: string }) {
  return (
    <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-[#f7d068]">{children}</p>
  );
}

/**
 * Photo plein cadre, fondue dans le bleu nuit vers le bas (le texte et la
 * preuve restent lisibles sur n'importe quelle image).
 */
function Photo({
  src,
  focus,
  tall = false,
  eager = false,
}: {
  src: string;
  focus: string;
  /** Accueil : la photo occupe le haut de l'écran, sous le texte. */
  tall?: boolean;
  eager?: boolean;
}) {
  return (
    <div
      aria-hidden
      className={cn('absolute inset-x-0 top-0 overflow-hidden', tall ? 'h-[68%]' : 'h-full')}
    >
      <img
        src={src}
        alt=""
        draggable={false}
        loading={eager ? 'eager' : 'lazy'}
        decoding="async"
        className={cn('h-full w-full object-cover', focus)}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#061433]/55 via-[#061433]/0 via-35% to-[#061433]" />
    </div>
  );
}
